import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Band from "../../../assets/icons/Channel/band_mini.svg";
import Blog from "../../../assets/icons/Channel/blog_mini.svg";
import Feed from "../../../assets/icons/Channel/feed_mini.svg";
import Kakao from "../../../assets/icons/Channel/kakao_mini.svg";
import X from "../../../assets/icons/Channel/x_mini.svg"
import MMS from "../../../assets/icons/Channel/mms_mini.svg"
import { Chip } from "../../../assets/components/Chip";
import close from "../../../assets/icons/System/close.svg";
import { HeaderIconLR } from "../../../assets/components/HeaderGroup";
import { useFixedLayout } from "../../../hooks/useFixedLayout";
import up from "../../../assets/icons/Chevron/up_black.svg";
import down from "../../../assets/icons/Chevron/down_black.svg";
import left from "../../../assets/icons/Chevron/left_black.svg";
import right from "../../../assets/icons/Chevron/right_black.svg";
import left_disabled from "../../../assets/icons/Chevron/left_disabled.svg";
import right_disabled from "../../../assets/icons/Chevron/right_disabled.svg";
import gridOn from "../../../assets/icons/Calendar/show_grid_default.svg";
import gridOff from "../../../assets/icons/Calendar/show_grid_disabled.svg";
import listOn from "../../../assets/icons/Calendar/show_list_default.svg";
import listOff from "../../../assets/icons/Calendar/show_list_disabled.svg";
import arrow from "../../../assets/icons/Arrow/swap.svg";
import error from "../../../assets/icons/warning/error_gray.svg";
import info from "../../../assets/icons/System/info.svg";
import Popup from "../../../assets/components/PopupGroup";
import { FillButton, Button } from "../../../assets/components/ButtonGroup";
import TokenPopup from "../../Ads/components/detailComponents/TokenPopup";
import { postToApp } from "../../../bridges/appBridge";
import useInitAppDataStore from "../../../stores/initAppData";
import "dayjs/locale/ko";
dayjs.locale("ko");
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

/* =========================
 *  상수 / 유틸 함수 (컴포넌트 밖)
 * ========================= */

const channelMap = {
  1: "카카오톡",
  2: "인스타그램",
  3: "인스타그램",
  4: "블로그",
  5: "MMS(문자)",
  6: "네이버 밴드",
  7: "X(트위터)"
};

const channelIconMap = {
  카카오톡: Kakao,
  인스타그램: Feed,
  블로그: Blog,
  "MMS(문자)": MMS,
  "네이버 밴드": Band,
  "X(트위터)": X
};

const channelColorMap = {
  인스타그램: "#D62976",
  블로그: "#03C75A",
  "네이버 밴드": "#03C75A",
  카카오톡: "#CEBB05",
  "MMS(문자)": "#4E79E5",
  "X(트위터)": "#7E7E7E"
};

const withAlphaHex = (hex, alphaHex = "1F") =>
  `${hex}${alphaHex}`; // "#RRGGBB" -> "#RRGGBBAA"

const dotColorOverrideMap = {
  인스타그램: "#FB4897",
  블로그: "#00EE65",
  "네이버 밴드": "#00EE65",
  카카오톡: "#EFD800",
  "MMS(문자)": "#4E79E5",
  "X(트위터)": "#7E7E7E"
};

const filterColorMap = Object.fromEntries(
  Object.entries(channelColorMap).map(([k, hex]) => [
    k,
    { main: hex, bg: withAlphaHex(hex, "1F"), dot: dotColorOverrideMap[k] ?? hex, },
  ])
);

const titleMap = {
  1: "매장 홍보",
  2: "상품 소개",
  3: "이벤트",
};

// 날짜 파싱 유틸들(전역 함수로 빼서 Hook deps 이슈 제거)
const parseDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const dateFromImagePath = (path) => {
  if (!path) return null;
  const m = path.match(/_(\d{14})\./); // e.g. ..._20251120073232.png
  if (!m) return null;
  const s = m[1];
  const d = new Date(
    Number(s.slice(0, 4)),
    Number(s.slice(4, 6)) - 1,
    Number(s.slice(6, 8)),
    Number(s.slice(8, 10)),
    Number(s.slice(10, 12)),
    Number(s.slice(12, 14))
  );
  return Number.isNaN(d.getTime()) ? null : d;
};

const pickDate = (r) =>
  parseDate(r.created_at) ||
  parseDate(r.updated_at) ||
  dateFromImagePath(r.image_path) ||
  parseDate(r.end_date) ||
  parseDate(r.start_date) ||
  new Date(0);

const EmptyState = ({ isToday = false, onCreateClick = () => { } }) => {
  return (
    <div className="flex flex-col items-center justify-center">
      <img src={error} alt="" />
      <p className="pt-[20px] text-t4 text-text-default">
        이미지 생성 내역이 없어요
      </p>

      {isToday && (
        <>
          <p className="pt-[4px] text-b2 text-text-subtler">
            AI가 추천하는 이미지를 생성해 보세요
          </p>
          <div className="pt-[16px] w-[239px]">
            <Button
              type="fill"
              onClick={onCreateClick}
              size="sm"
              className="text-b2"
            >
              AI 마케팅 하기
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

/* =========================
 *  메인 컴포넌트
 * ========================= */

const MyCalendar = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem("user_id");
  const location = useLocation();
  const tab = location.state?.tab;
  const initialChannelFilter = location.state?.initialChannelFilter;

  // initAppDataStore에서 초기 스냅샷 가져오기
  const { getInitAppData } = useInitAppDataStore();
  const initAppData = getInitAppData(Number(userId));
  const navAppData = location.state?.data;
  const appData = initAppData || navAppData || null;

  const storeBusinessNumber = location.state?.store_business_number || appData?.store_business_number || localStorage.getItem("store_business_number");

  const minMonth = appData?.register_date
    ? dayjs(appData.register_date).startOf("month")
    : null;
  const maxMonth = dayjs();

  const [channelFilter, setChannelFilter] = useState("all");
  const { headerRef, headerSpace } = useFixedLayout();
  const [open, setOpen] = React.useState(false);
  const [tokenAll, setTokenAll] = useState(0);
  const [showTokenModal, setShowTokenModal] = useState(false);

  const [records, setRecords] = useState([]);

  const today = dayjs();
  const [currentDate, setCurrentDate] = useState(today);
  const [selectedDay, setSelectedDay] = useState(null);

  const isPrevDisabled =
    !!minMonth && currentDate.isSame(minMonth, "month");
  const isNextDisabled = currentDate.isSame(maxMonth, "month");

  const [viewMode, setViewMode] = useState("day"); // "day" | "month"
  const [layoutMode, setLayoutMode] = useState("grid"); // "grid" | "list"
  const [sortOrder, setSortOrder] = useState("desc"); // "desc" | "asc"
  const [isFolded, setIsFolded] = useState(false); // false = 전체, true = 한 줄만
  const isTodayChipDisabled = viewMode === "month" || isFolded;

  const isSelectedDayToday = selectedDay && selectedDay.isSame(dayjs(), "day");
  const isCurrentMonthToday = currentDate.isSame(dayjs(), "month");

  const onCreate = () => {
    if (tokenAll <= 0) {
      setShowTokenModal(true);
      return;
    }

    if (!appData) {
      console.warn("onCreate 호출 시 appData 없음");
      return;
    }

    const baseData = {
      ...appData,
      store_business_number: appData.store_business_number,
      main: appData.main ?? "",
      temp: appData.temp ?? 0,
      store_name: appData.store_name,
      road_name: appData.road_name,
      district_name: appData.district_name,
      detail_category_name: appData.detail_category_name,
    };

    const manualOptions = {
      category: "",
      age: appData.ai_age,
      channel: "",
      subChannel: "",
      theme: "",
      customMenu: appData.custom_menu || "",
      customText: "",
      prompt: "",
      style: "",
      aiThumbnail: "",
      aiAge: "",
      aiChannel: "",
      aiTitle: "",
      aiStyle: "",
    };

    const manualData = {
      ...baseData,
      ...manualOptions,
    };

    navigate(`/manual/marketing/ai`, {
      state: manualData,
    });
  };

  // 👇 이 부분 추가
  useEffect(() => {
    try {
      // 이 페이지에 들어왔을 때: 상태바를 원하는 색으로 요청
      postToApp({
        type: "SET_STATUS_BAR",             // 약속한 이름 (RN 개발자랑 맞추기)
        payload: {
          backgroundColor: "#F5F6F9",       // 예: 흰색
          style: "dark",                    // "dark" = 어두운 아이콘, "light" = 흰 아이콘
        },
      });
    } catch (e) {
      console.warn("SET_STATUS_BAR postToApp 실패:", e);
    }

    // 페이지 빠져나갈 때 원래 상태로 돌려달라고 요청 (옵션)
    return () => {
      try {
        postToApp({
          type: "SET_STATUS_BAR",
          payload: {
            backgroundColor: "#FFFFFF",     // 예: 기본 보라색 같은 거
            style: "dark",
          },
        });
      } catch (e) {
        console.warn("RESET_STATUS_BAR postToApp 실패:", e);
      }
    };
  }, []);

  // 최초 한번: 전체 기록 + 토큰
  useEffect(() => {
    const sendGenerateRequest = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/get/user/info`,
          { userId },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        const rawRecords = response.data.record || [];
        const normalized = Array.isArray(rawRecords) ? rawRecords : [rawRecords];
        setRecords(normalized);
        setTokenAll(response.data.ticket_info.token_amount || 0);
      } catch (error) {
        console.error("자동화 광고 생성 중 오류:", error);
        alert("광고 생성에 실패했습니다. 다시 시도해주세요.");
      }
    };
    sendGenerateRequest();
  }, [userId]);

  // 정렬 함수 (pickDate는 컴포넌트 밖 전역 함수라 deps 필요 없음)
  const sortByOrder = useCallback(
    (a, b) => {
      return sortOrder === "desc"
        ? pickDate(b) - pickDate(a)
        : pickDate(a) - pickDate(b);
    },
    [sortOrder]
  );

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };
  const sortLabel = sortOrder === "desc" ? "최신순" : "오래된 순";

  // 달력용 날짜 배열
  const daysArray = [];
  const monthStart = currentDate.startOf("month");
  const dayOffset = monthStart.day();
  const dayCount = currentDate.daysInMonth();
  const needsSixWeeks = dayOffset + dayCount > 35;
  const totalSlots = needsSixWeeks ? 42 : 35;

  for (let i = 0; i < dayOffset; i++) {
    daysArray.push({ date: null, type: "empty" });
  }
  for (let i = 0; i < dayCount; i++) {
    daysArray.push({
      date: monthStart.add(i, "day"),
      type: "current",
    });
  }
  while (daysArray.length < totalSlots) {
    daysArray.push({ date: null, type: "empty" });
  }

  const goPrevMonth = () => {
    setCurrentDate((prev) => {
      const target = prev.subtract(1, "month");

      // register_date가 있고, target이 그보다 이전 달이면 막기
      if (minMonth && target.isBefore(minMonth, "month")) {
        return prev; // 그대로 유지 (이전 달로 안 감)
      }

      return target;
    });

    setChannelFilter("all");
  };

  const goNextMonth = () => {
    setCurrentDate((prev) => {
      const target = prev.add(1, "month");

      // 현재 월(target)이 maxMonth(현재 달)보다 이후라면 막기
      if (target.isAfter(maxMonth, "month")) {
        return prev; // 그대로 유지 (다음 달로 안 감)
      }

      return target;
    });

    setChannelFilter("all");
  };

  const handleDayClick = (dateObj) => {
    if (!dateObj) return;
    setSelectedDay(dateObj);
    setChannelFilter("all");
  };

  // daysArray를 주(week) 단위로 자르기
  const weeks = []
  for (let i = 0; i < daysArray.length; i += 7) {
    weeks.push(daysArray.slice(i, i + 7));
  }

  const visibleWeeks = (() => {
    if (!isFolded) return weeks; // 전체 달 보기

    if (viewMode === "month") {
      return [weeks[0]];
    }

    // 접힌 상태: selectedDay가 포함된 주 찾기
    const selectedWeek = weeks.find((week) =>
      week.some((item) => item.date && selectedDay && item.date.isSame(selectedDay, "day"))
    );

    if (selectedWeek) return [selectedWeek];

    // 만약 이번 달에 오늘이 없다면(다른 달 보고 있는 경우) 첫 주만 보여주기
    return [weeks[0]];
  })();

  // 특정 날짜에 표시할 채널 점들
  const getChannelsForDay = (dateObj) => {
    if (!dateObj) return [];

    const result = [];

    records.forEach((rec) => {
      const start = dayjs(rec.start_date);
      const end = dayjs(rec.end_date);
      const repeat = rec.repeat_time || "없음";
      const channel = channelMap[rec.channel];

      if (!channel) return;
      if (dateObj.isBefore(start, "day") || dateObj.isAfter(end, "day")) return;

      switch (repeat) {
        case "매일":
          result.push(channel);
          break;
        case "매주":
          if (dateObj.day() === start.day()) {
            result.push(channel);
          }
          break;
        case "매월":
          if (dateObj.date() === start.date()) {
            result.push(channel);
          }
          break;
        case "없음":
        default:
          if (dateObj.isSame(start, "day")) {
            result.push(channel);
          }
          break;
      }
    });

    return [...new Set(result)];
  };

  // 공통: 특정 날짜의 기록
  const getRecordsForDay = useCallback(
    (day) => {
      if (!day) return [];

      return records
        .filter((rec) => {
          const start = dayjs(rec.start_date);
          const end = dayjs(rec.end_date);
          const repeat = rec.repeat_time || "없음";

          if (day.isBefore(start, "day") || day.isAfter(end, "day")) {
            return false;
          }

          switch (repeat) {
            case "매일":
              return true;
            case "매주":
              return day.day() === start.day();
            case "매월":
              return day.date() === start.date();
            case "없음":
            default:
              return day.isSame(start, "day");
          }
        })
        .filter((rec) => rec.image_path)
        .sort(sortByOrder);
    },
    [records, sortByOrder]
  );

  const getRecordsForSelectedDay = () => getRecordsForDay(selectedDay);

  const getRecordsForCurrentMonth = () => {
    const monthStart = currentDate.startOf("month");
    const monthEnd = currentDate.endOf("month");

    return records
      .filter((rec) => {
        const start = dayjs(rec.start_date);
        const end = dayjs(rec.end_date);

        if (!start.isValid() || !end.isValid()) return false;
        if (end.isBefore(monthStart, "day") || start.isAfter(monthEnd, "day")) {
          return false;
        }

        return !!rec.image_path;
      })
      .sort(sortByOrder);
  };

  // 첫 진입 시 오늘 날짜 선택
  useEffect(() => {
    setSelectedDay(dayjs());
    setChannelFilter("all");
  }, []);

  // MyRecord에서 넘어온 initialChannelFilter 처리
  useEffect(() => {
    if (!initialChannelFilter || records.length === 0) return;

    const todayDayjs = dayjs();
    const todaysRecords = getRecordsForDay(todayDayjs);

    const hasChannelToday = todaysRecords.some(
      (rec) => channelMap[rec.channel] === initialChannelFilter
    );

    setCurrentDate(todayDayjs);
    setSelectedDay(todayDayjs);
    setViewMode("day");
    setChannelFilter(hasChannelToday ? initialChannelFilter : "all");
  }, [records, initialChannelFilter, getRecordsForDay]);

  const handleHome = () => {
    if (tab === "mypage") {
      navigate("/ads/main", { state: { tab: "mypage" } });
      return;
    }
    navigate("/ads/main", { state: { storeBusinessNumber } });
  };

  const monthRecords = getRecordsForCurrentMonth();
  const dayRecords = getRecordsForSelectedDay();

  const getChannelCountsForList = (list) => {
    const counts = { kakao: 0, instagram: 0, band: 0, x:0, mms: 0 };

    list.forEach((rec) => {
      switch (rec.channel) {
        case 1:
          counts.kakao += 1;
          break;
        case 2:
        case 3:
          counts.instagram += 1;
          break;
        case 5:
          counts.mms += 1;
          break;
        case 6:
          counts.band += 1;
          break;
        case 7:
          counts.x += 1;
          break;
        default:
          break;
      }
    });

    return counts;
  };

  const dayChannelCounts = getChannelCountsForList(dayRecords);
  const monthChannelCounts = getChannelCountsForList(monthRecords);

  const viewChannelCounts =
    viewMode === "day" ? dayChannelCounts : monthChannelCounts;
  const totalCount =
    viewMode === "day" ? dayRecords.length : monthRecords.length;

  const applyChannelFilter = (list) =>
    list.filter((rec) => {
      if (channelFilter === "all") return true;
      const chName = channelMap[rec.channel];
      if (channelFilter === "인스타그램") return chName === "인스타그램";
      if (channelFilter === "네이버 밴드") return chName === "네이버 밴드";
      if (channelFilter === "카카오톡") return chName === "카카오톡";
      if (channelFilter === "MMS(문자)") return chName === "MMS(문자)";
      if (channelFilter === "X(트위터)") return chName === "X(트위터)";
      return true;
    });

  const filteredDayRecords = applyChannelFilter(dayRecords);
  const filteredMonthRecords = applyChannelFilter(monthRecords);

  return (
    <div
      className="min-h-dvh overflow-y-auto bg-surface-light-white"
      style={{
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      <style>
        {`
          div::-webkit-scrollbar {
            display: none;
          }   
        `}
      </style>
      <div className="bg-bg-white-subtle">
        <div ref={headerRef} className="fixed top-0 left-0 right-0 z-10">
          <HeaderIconLR
            title="마케팅 기록"
            leftIcon={close}
            onLeftClick={handleHome}
            className="bg-bg-white-subtle"
            rightIcon={info}
            onRightClick={() => setOpen(true)}
          />
          <Popup
            isOpen={open}
            onClose={() => setOpen(false)}
            title="마케팅 기록"
            subtitle={
              "마케팅 기록은 전체 활동을 확인할 수 있으나, 이미지 썸네일 내역은 최근 3개월까지만 조회할 수 있습니다."
            }
            renderButtons={
              <>
                <FillButton onClick={() => setOpen(false)}>확인</FillButton>
              </>
            }
          />
        </div>

        <div
          className="w-full overflow-auto scrollbar-none"
          style={{ paddingTop: headerSpace }}
        >
          {/* 일 / 월 토글 */}
          <div className="flex items-center justify-center">
            <div className="inline-flex items-center rounded-full bg-surface-light-white border p-[4px]">
              <button
                className={`px-[12px] py-[4px] rounded-full text-t4 leading-none transition w-[80px]
                  ${viewMode === "day"
                    ? "bg-surface-light-primary-light text-text-primary"
                    : "bg-transparent text-text-disabled text-b1"
                  }
                `}
                onClick={() => {
                  setViewMode("day");
                  setChannelFilter("all");
                }}
              >
                일
              </button>
              <button
                className={`px-[12px] py-[4px] rounded-full text-t4 leading-none transition w-[80px]
                  ${viewMode === "month"
                    ? "bg-surface-light-primary-light text-text-primary"
                    : "bg-transparent text-text-disabled text-b1"
                  }
                `}
                onClick={() => {
                  setViewMode("month");
                  setChannelFilter("all");
                }}
              >
                월
              </button>
            </div>
          </div>

          {/* 달력 카드 */}
          <div className="px-[16px] pt-[8px] pb-[16px]">
            <div className="rounded-[24px] bg-surface-light-white border border-border-subtle px-[4px] pt-[12px]">
              {/* 달력 헤더 */}
              <div className="flex items-center justify-between px-[8px]">
                <div className="flex items-center gap-[8px]">
                  <button onClick={goPrevMonth} disabled={isPrevDisabled}>
                    <img src={isPrevDisabled ? left_disabled : left} alt="헤더 왼쪽" />
                  </button>
                  <span className="text-t3 w-[120px] text-center">
                    {currentDate.format("YYYY년 M월")}
                  </span>
                  <button onClick={goNextMonth} disabled={isNextDisabled}>
                    <img src={isNextDisabled ? right_disabled : right} alt="헤더 오른쪽" />
                  </button>
                </div>

                <Chip
                  chip="c"
                  status={isTodayChipDisabled ? "disabled" : "default"}
                  label="오늘"
                  onClick={() => {
                    if (isTodayChipDisabled) return; // 비활성일 땐 아무 것도 안 함
                    setCurrentDate(today);
                    setSelectedDay(today);
                    setViewMode("day");
                    setChannelFilter("all");
                  }}
                />
              </div>

              {/* 요일 헤더 */}
              <div className="grid grid-cols-7 text-center text-b2 text-text-subtle pb-[4px] pt-[20px]">
                {"일월화수목금토".split("").map((d) => (
                  <div
                    key={d}
                    className="h-6 flex items-center justify-center"
                  >
                    {d}
                  </div>
                ))}
              </div>

              {/* 날짜 셀 */}
              <div className="space-y-[4px]">
                {visibleWeeks.map((week, weekIdx) => (
                  <div
                    key={weekIdx}
                    className="grid grid-cols-7 text-c3 text-text-default"
                  >
                    {week.map((item, idx) => {
                      const isToday = item.date && item.date.isSame(today, "day");

                      const isSelected =
                        item.date &&
                        viewMode === "day" &&
                        selectedDay &&
                        item.date.isSame(selectedDay, "day");

                      const dayChannels = item.date ? getChannelsForDay(item.date) : [];
                      const displayChannels = dayChannels;

                      if (item.type === "empty") {
                        return <div key={idx} />;
                      }

                      let dayBgClass = "bg-surface-light-subtle text-text-default";
                      if (isSelected) {
                        dayBgClass = "bg-surface-light-primary text-text-inverse";
                      } else if (isToday && viewMode === "day") {
                        dayBgClass = "bg-surface-light-primary-light text-text-primary"; // 오늘이지만 선택은 안 됐을 때
                      }

                      return (
                        <div
                          key={idx}
                          className="flex flex-col items-center justify-start pt-[12px] pb-[4px] cursor-pointer"
                          onClick={() =>
                            viewMode === "day" &&
                            item.type === "current" &&
                            handleDayClick(item.date)
                          }
                        >
                          <div
                            className={`w-[32px] h-[32px] rounded-full flex items-center justify-center
                              ${dayBgClass}`}
                          >
                            {item.date && (
                              <span className="text-c3">
                                {item.date.date()}
                              </span>
                            )}
                          </div>

                          <div className="mt-[4px] flex items-center justify-center gap-[4px]">
                            {displayChannels.map((channel, i) => (
                              <span
                                key={i}
                                className="w-[4px] h-[4px] rounded-full"
                                style={{
                                  backgroundColor:
                                    channelColorMap[channel] || "#ccc",
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              <div className="w-full">
                <button
                  type="button"
                  onClick={() => setIsFolded((prev) => !prev)}
                  className="w-full h-[36px] flex items-center justify-center"
                >
                  <img src={isFolded ? down : up} alt="달력 접기/열기" />
                </button>
              </div>
            </div>
          </div>

          {/* 선택된 날짜 / 월별 이미지 영역 */}
          <div className="bg-surface-light-white px-[16px] py-[16px] flex-1">
            {/* 채널 필터 */}
            <div className="flex gap-2 pb-[20px] overflow-x-auto overscroll-x-contain scrollbar-none flex-nowrap">
              <button
                type="button"
                onClick={() => setChannelFilter("all")}
                className={`flex items-center rounded-full px-4 py-2 border text-c1 whitespace-nowrap shrink-0
                  ${channelFilter === "all"
                    ? "bg-surface-light-primary-light border-border-primary text-text-primary"
                    : "bg-surface-light-white border-border-default text-text-subtler text-c2"
                  }
                `}
              >
                전체
                <span className="pl-[6px]">{totalCount}</span>
              </button>

              {[
                {
                  key: "인스타그램",
                  label: "인스타그램",
                  count: viewChannelCounts.instagram,
                },
                {
                  key: "카카오톡",
                  label: "카카오톡",
                  count: viewChannelCounts.kakao,
                },
                {
                  key: "네이버 밴드",
                  label: "네이버 밴드",
                  count: viewChannelCounts.band,
                },
                {
                  key: "X(트위터)",
                  label: "X(트위터)",
                  count: viewChannelCounts.x,
                },
                {
                  key: "MMS(문자)",
                  label: "MMS(문자)",
                  count: viewChannelCounts.mms,
                },
              ].map((opt) => {
                const hasPosts = opt.count > 0;
                const isActive = channelFilter === opt.key;
                const colors = filterColorMap[opt.key] || {};
                const mainColor = colors.main;
                const activeBg = colors.bg || "rgba(209, 213, 219, 0.16)";
                const dotColor = colors.dot ?? mainColor;

                const baseClass =
                  "flex items-center justify-between rounded-full px-[12px] py-[8px] border text-c1 whitespace-nowrap shrink-0";

                const stateClass = !hasPosts
                  ? "bg-surface-light-white border-border-subtle text-text-disabled text-c2"
                  : isActive
                    ? "border-[transparent] text-text-primary"
                    : "bg-surface-light-white border-border-default text-text-subtler text-c2";

                return (
                  <button
                    key={opt.key}
                    type="button"
                    disabled={!hasPosts}
                    onClick={() => {
                      if (!hasPosts) return;
                      setChannelFilter(opt.key);
                    }}
                    className={`${baseClass} ${stateClass}`}
                    style={{
                      backgroundColor:
                        hasPosts && isActive ? activeBg : undefined,
                      borderColor:
                        hasPosts && isActive ? mainColor : undefined,
                      color: hasPosts && isActive ? mainColor : undefined,
                      opacity: hasPosts ? 1 : 0.7,
                      cursor: hasPosts ? "pointer" : "default",
                    }}
                  >
                    <div className="flex items-center gap-[6px]">
                      {hasPosts && isActive ? (
                        <span
                          style={{ color: mainColor }}
                          className="text-base leading-none"
                        >
                          ✓
                        </span>
                      ) : (
                        <span
                          className="w-[4px] h-[4px] rounded-full"
                          style={{
                            backgroundColor: dotColor,
                          }}
                        />
                      )}

                      <span>{opt.label}</span>
                    </div>

                    <span className="pl-[6px] text-c2">{opt.count}</span>
                  </button>
                );
              })}
            </div>

            {viewMode === "day" ? (
              selectedDay && (
                <>
                  <div className="flex items-center justify-between pb-[16px]">
                    <p className="text-t3 pb-[4px]">
                      {selectedDay.format("YYYY년 M월 D일")}
                    </p>
                    <button
                      type="button"
                      onClick={toggleSortOrder}
                      className="flex items-center text-b1 text-text-subtler"
                    >
                      <span>{sortLabel}</span>
                      <img src={arrow} alt="정렬 변경" />
                    </button>
                  </div>

                  <div className="flex justify-end items-center gap-[8px] pb-[8px]">
                    <div className="flex items-center gap-[8px]">
                      <button
                        type="button"
                        onClick={() => setLayoutMode("grid")}
                        className="flex items-center justify-center"
                      >
                        <img
                          src={layoutMode === "grid" ? gridOn : gridOff}
                          alt="그리드 보기"
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => setLayoutMode("list")}
                        className="flex items-center justify-center"
                      >
                        <img
                          src={layoutMode === "list" ? listOn : listOff}
                          alt="리스트 보기"
                        />
                      </button>
                    </div>
                  </div>

                  {filteredDayRecords.length === 0 ? (
                    <EmptyState
                      isToday={isSelectedDayToday}
                      onCreateClick={onCreate}
                    />
                  ) : layoutMode === "grid" ? (
                    <div className="grid grid-cols-3 gap-[8px]">
                      {filteredDayRecords.map((rec, idx) => (
                        <div
                          key={`${rec.id || idx}`}
                          className="w-full aspect-square relative overflow-hidden rounded-[8px] cursor-pointer"
                          onClick={() =>
                            navigate("/mycalendar/detail", {
                              state: {
                                record: rec,
                                from: "calendar",
                                tab,
                                storeBusinessNumber,
                                tokenAll,
                              },
                            })
                          }
                        >
                          <img
                            src={rec.image_path}
                            alt="기록 이미지"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute bottom-1 left-1 text-center rounded pr-1 flex flex-row items-center">
                            <img
                              src={
                                channelIconMap[channelMap[rec.channel]]
                              }
                              alt=""
                              className="pr-1"
                            />
                            <span className="text-white text-c2">
                              {titleMap[rec.title]}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-[12px]">
                      {filteredDayRecords.map((rec, idx) => (
                        <div
                          key={`${rec.id || idx}`}
                          className="rounded-[12px] bg-surface-light-subtle border border-border-subtle p-[16px] text-center"
                          onClick={() =>
                            navigate("/mycalendar/detail", {
                              state: {
                                record: rec,
                                from: "calendar",
                                tab,
                                storeBusinessNumber,
                                tokenAll,
                              },
                            })
                          }
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-[4px]">
                              <img
                                src={channelIconMap[channelMap[rec.channel]]}
                                alt=""
                                className="w-[24px] h-[24px]"
                              />
                              <span className="text-t4 text-text-subtler">
                                {titleMap[rec.title]}
                              </span>
                            </div>
                          </div>
                          <div className="pt-[24px] pb-[16px] overflow-hidden text-center flex items-center justify-center">
                            <img
                              src={rec.image_path}
                              alt="기록 이미지"
                              className={`object-cover ${[1, 2].includes(rec.channel)
                                ? "w-[180px] rounded-[12px]"
                                : [3, 6].includes(rec.channel)
                                  ? "w-[200px] rounded-[16px]"
                                  : ""
                                }`}
                            />
                          </div>
                          <div className="flex-1 flex flex-col gap-[4px]">
                            <p className="text-c2 text-text-subtler pt-[16px] text-left">
                              {rec.insta_copyright}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )
            ) : (
              <>
                <div className="flex items-center justify-between pb-[16px]">
                  <p className="text-t3 pb-[4px]">
                    {currentDate.format("YYYY년 M월")}
                  </p>
                  <button
                    type="button"
                    onClick={toggleSortOrder}
                    className="flex items-center text-b1 text-text-subtler"
                  >
                    <span>{sortLabel}</span>
                    <img src={arrow} alt="정렬 변경" />
                  </button>
                </div>
                <div className="flex justify-end items-center gap-[8px] pb-[8px]">
                  <div className="flex items-center gap-[8px]">
                    <button
                      type="button"
                      onClick={() => setLayoutMode("grid")}
                      className="flex items-center justify-center"
                    >
                      <img
                        src={layoutMode === "grid" ? gridOn : gridOff}
                        alt="그리드 보기"
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => setLayoutMode("list")}
                      className="flex items-center justify-center"
                    >
                      <img
                        src={layoutMode === "list" ? listOn : listOff}
                        alt="리스트 보기"
                      />
                    </button>
                  </div>
                </div>

                {filteredMonthRecords.length === 0 ? (
                  <EmptyState
                    isToday={isCurrentMonthToday}
                    onCreateClick={onCreate}
                  />
                ) : layoutMode === "grid" ? (
                  <div className="grid grid-cols-3 gap-[8px] pb-[16px]">
                    {filteredMonthRecords.map((rec, idx) => (
                      <div
                        key={`${rec.id || idx}`}
                        className="w-full aspect-square relative overflow-hidden rounded-[8px] cursor-pointer"
                        onClick={() =>
                          navigate("/mycalendar/detail", {
                            state: {
                              record: rec,
                              from: "calendar",
                              tab,
                              storeBusinessNumber,
                              tokenAll,
                            },
                          })
                        }
                      >
                        <img
                          src={rec.image_path}
                          alt="기록 이미지"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-1 left-1 text-center rounded pr-1 flex flex-row items-center">
                          <img
                            src={channelIconMap[channelMap[rec.channel]]}
                            alt=""
                            className="pr-1"
                          />
                          <span className="text-white text-c2">
                            {titleMap[rec.title]}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-[12px]">
                    {filteredMonthRecords.map((rec, idx) => (
                      <div
                        key={`${rec.id || idx}`}
                        className="rounded-[12px] bg-surface-light-subtle border border-border-subtle p-[16px] text-center"
                        onClick={() =>
                          navigate("/mycalendar/detail", {
                            state: {
                              record: rec,
                              from: "calendar",
                              tab,
                              storeBusinessNumber,
                              tokenAll,
                            },
                          })
                        }
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-[4px]">
                            <img
                              src={channelIconMap[channelMap[rec.channel]]}
                              alt=""
                              className="w-[24px] h-[24px]"
                            />
                            <span className="text-t4 text-text-subtler">
                              {titleMap[rec.title]}
                            </span>
                          </div>
                        </div>
                        <div className="pt-[24px] pb-[16px] overflow-hidden text-center flex items-center justify-center">
                          <img
                            src={rec.image_path}
                            alt="기록 이미지"
                            className={`object-cover ${[1, 2].includes(rec.channel)
                              ? "w-[180px] rounded-[12px]"
                              : [3, 6].includes(rec.channel)
                                ? "w-[200px] rounded-[16px]"
                                : ""
                              }`}
                          />
                        </div>
                        <div className="flex-1 flex flex-col gap-[4px]">
                          <p className="text-c2 text-text-subtler pt-[16px] text-left">
                            {rec.insta_copyright}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <TokenPopup
        isOpen={showTokenModal}
        onClose={() => setShowTokenModal(false)}
        origin="myCalendar"
      />
    </div>
  );
};

export default MyCalendar;
